import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, BadgeCheck } from "lucide-react";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const LINKS = [
  { label: "Avantages", href: "#avantages" },
  { label: "Devis", href: "#devis" },
  { label: "Réalisations", href: "#realisations" },
  { label: "Avis", href: "#avis" },
  { label: "FAQ", href: "#faq" },
  { label: "Pompe à chaleur", href: "/", highlight: true },
];

export default function ClimNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    if (!href.startsWith("#")) {
      setOpen(false);
      return;
    }
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="clim-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "border-b border-glacier-border bg-white/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-[72px] sm:px-8">
        <a
          href="#"
          data-testid="clim-nav-logo"
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            scrollToTop();
          }}
          className="flex items-center gap-2.5"
        >
          <img src="/logo-enersolutions.png" alt="Ener Solutions" className="h-10 w-auto" />
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`clim-nav-link-${l.href.slice(1) || "pac"}`}
              onClick={(e) => go(e, l.href)}
              className={`text-sm font-medium transition-colors ${
                l.highlight
                  ? "rounded-full border border-forest/30 px-3 py-1 text-forest hover:bg-forest/5"
                  : "text-slate-600 hover:text-ice"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-4 lg:flex">
          <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <BadgeCheck className="h-3.5 w-3.5" /> Installateur RGE
          </span>
          <a
            href="tel:+33535759803"
            data-testid="clim-nav-phone"
            className="flex items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-ice"
          >
            <Phone className="h-4 w-4" /> 05 35 75 98 03
          </a>
          <button
            data-testid="clim-nav-cta"
            onClick={(e) => go(e, "#devis")}
            className="rounded-full bg-ice px-5 py-2.5 text-sm font-bold text-white transition-all hover:scale-[1.03] hover:bg-ice-hover"
          >
            Devis gratuit
          </button>
        </div>

        <button
          data-testid="clim-nav-menu-button"
          className="grid h-10 w-10 place-items-center rounded-lg border border-glacier-border bg-white text-ink lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-glacier-border bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`clim-nav-mobile-link-${l.href.slice(1) || "pac"}`}
                  onClick={(e) => go(e, l.href)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-glacier hover:text-ice"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+33535759803"
                data-testid="clim-nav-mobile-phone"
                className="mt-2 flex items-center gap-2 rounded-lg px-3 py-3 text-base font-semibold text-ice"
              >
                <Phone className="h-4 w-4" /> 05 35 75 98 03
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
